import React from "react";
import { StriimModal, StriimButton, StriimTypography, StriimDataTable } from "@striim/striim-ui";
import { Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { optionsType } from "./create-app-page-app-type-dropdown";
import { adapterType } from "../create-app-page-templates";

export interface DraftWizardsTypes {
    appName: string;
    namespace: string;
    url: string;
    currentStep: string;
    date: string;
}

interface DraftAdaptersSelectionModalTypes {
    isDraftsAvailable: boolean;
    setIsDraftsAvailable: React.Dispatch<React.SetStateAction<boolean>>;
    templateInfo: optionsType;
    draftWizards: DraftWizardsTypes[];
    sourceAdapterInfo: adapterType;
    targetAdapterInfo: adapterType;
}

const CellItem = ({ value }) => (
    <StriimTypography variant="body4" color={"greyscale.900"}>
        {value}
    </StriimTypography>
);

const Columns = [
    {
        headerName: "App Name",
        field: "appName",
        sortable: true,
        resizable: true,
        flex: 2,
        cellRenderer: ({ value }) => <CellItem value={value} />
    },
    {
        headerName: "Namespace",
        field: "namespace",
        sortable: true,
        resizable: true,
        flex: 1.2,
        cellRenderer: ({ value }) => <CellItem value={value} />
    },
    {
        headerName: "Current Step",
        field: "currentStep",
        sortable: true,
        resizable: true,
        flex: 2,
        cellRenderer: ({ value }) => <CellItem value={value} />
    },
    {
        headerName: "Last Saved On",
        field: "date",
        sortable: true,
        resizable: true,
        flex: 2,
        cellRenderer: ({ value }) => <CellItem value={value} />
    },
    {
        headerName: "",
        field: "url",
        flex: 1.4,
        cellRenderer: ({ data }) => {
            const { namespace, appName, url } = data;
            const navigate = useNavigate();
            return (
                <StriimButton
                    data-test-id={`${namespace}.${appName}-resume-button`}
                    onClick={() => {
                        navigate(url);
                    }}
                >
                    Resume
                </StriimButton>
            );
        }
    }
];

const DraftAdaptersSelectionModal: React.FunctionComponent<DraftAdaptersSelectionModalTypes> = ({
    isDraftsAvailable,
    setIsDraftsAvailable,
    templateInfo,
    draftWizards,
    sourceAdapterInfo,
    targetAdapterInfo
}) => {
    const navigate = useNavigate();
    const sourceAdapter = sourceAdapterInfo?.id;
    const targetAdapter = targetAdapterInfo?.id;
    const appType = templateInfo?.label;

    return (
        <StriimModal
            autoHeight
            dialogProps={{
                sx: { ".MuiDialog-paper": { width: "auto", maxWidth: "900px" } },
                "data-test-id": "draft-wizards-modal"
            }}
            isVisible={isDraftsAvailable}
            titleContent={
                <StriimTypography variant="h2" color="primary.700">
                    Continue where you left off?
                </StriimTypography>
            }
            cancelContent={"Close"}
            confirmContent={"Create New"}
            onConfirm={() => {
                templateInfo?.url && navigate(templateInfo?.url);
            }}
            onCancel={() => {
                setIsDraftsAvailable(false);
            }}
        >
            <Grid container direction="column" spacing={2}>
                <Grid item>
                    <StriimTypography variant="body4" color="greyscale.800">
                        You have unfinished applications for the selected topology : {sourceAdapter} -&gt;{" "}
                        {targetAdapter} {appType && `(${appType})`}. Select one to resume, or create a new one.
                    </StriimTypography>
                </Grid>
                <Grid item>
                    <StriimDataTable data={draftWizards} columns={Columns} maxHeight={600} rowHeight={45} />
                </Grid>
            </Grid>
        </StriimModal>
    );
};

export default DraftAdaptersSelectionModal;
